import React, { useState, useEffect, useRef, useMemo } from 'react';
import { WebSocketAdapter } from '../../utils/WebSocketAdapter';
import { AppHeader, QRCodeDisplay, AvatarSelector, PlayerListSection, TeamAssignPanel, Modal, SettingsModal, BgAnimate, CategoryVote, CategoryResults, StatisticsModal, GamePremiumBadge } from '../shared';
import { AppIcon, HeaderIcons } from '../shared/icons';
import { isMonthlyFreeGame } from '../../config/monthlyFreeGames';
import { BurgerMenu } from '../menu/Menu';
import MenuPageWrapper from '../menu/MenuPageWrapper';
import HowToPlayPage from '../menu/HowToPlayPage';
import PremiumPage from '../menu/PremiumPage';
import HelpPage from '../menu/HelpPage';
import Footer from '../shared/Footer';
import GameCarousel from '../shared/GameCarousel';
import '../../App.css';
import '../../styles/waiting-room.css';
import '../../styles/player-list.css';
import '../../styles/lobby.css';
import './GameSelection.css';
import { avatars } from '../../assets/avatars';
import { useToast } from '../shared/ToastContext';
import { config, appVariant, ALL_GAMES } from '../../config';
import { GameConfig } from '../../config/types';
import RoomSessionStorage from '../../utils/sessionStorage';
import { gameHasCategories } from '../../config/categories';
import { getMenuGameLinks } from '../../config/gameLinks';
import { useTranslation } from '../../i18n';
import { getGameLinkName, translateBackendError, translateGames } from '../../i18n/helpers';
import { TeamsState, TeamId } from '../../types/teams';
import { gameUsesTeams, getTeamStartBlockReason } from '../../utils/teamGames';

type MenuPage = 'how-to-play' | 'premium' | 'support' | null;

const RoomCodeDisplay: React.FC<{ code: string }> = ({ code }) => {
  const { t } = useTranslation();
  return (
    <span className="host-qr-room-code-display" aria-label={t('lobby.gameSelection.roomCodeAria', { code })}>
      {code}
    </span>
  );
};

interface Player {
  id: string;
  name: string;
  score: number;
  avatar?: string;
  teamId?: 'A' | 'B';
}

interface RoomSettings {
  timerDuration?: number;
  category?: string;
  whosPlaying?: string;
  numberOfQuestions?: number;
}

interface GameSelectionProps {
  roomName: string;
  currentGameType?: string;
  votes?: Record<string, number>;
  categoryVotes?: Record<string, number>;
  players?: Player[];
  teams?: TeamsState;
  onSelectGame: (gameType: string) => void;
  onStartGame: (selectedGameType?: string) => void;
  socket: WebSocketAdapter;
  isHost?: boolean;
  myPlayerId?: string;
  settings?: RoomSettings;
  onGoHome?: () => void;
  hostIsPlaying?: boolean;
  onHostIsPlayingChange?: (isPlaying: boolean) => void;
  isPremium?: boolean;
  onJoinRoom?: () => void;
}

// Get games based on variant and premium status
// Premium rooms show variant-specific games (same as non-premium)
// Premium status allows access to premium games within the variant, not all games
const getGamesForRoom = (isPremium?: boolean): GameConfig[] => {
  // Premium rooms still use variant-specific games, but can access premium games within that variant
  return config.games;
};

// Game Carousel Tile Component
export interface GameCarouselTileProps {
  game: GameConfig;
  isActive: boolean;
  isSelected: boolean;
  votes: number;
  onClick: () => void;
  imageIndex?: number;
  setImageIndex?: (index: number) => void;
  blitzImages?: string[];
  isPremiumRoom?: boolean;
}

export const GameCarouselTile: React.FC<GameCarouselTileProps> = ({ game, isActive, isSelected, votes, onClick, imageIndex = 0, setImageIndex, blitzImages = [], isPremiumRoom = false }) => {
  const { t } = useTranslation();
  const showMonthlyFree = game.premium && !isPremiumRoom && isMonthlyFreeGame(game.id);

  return (
    <button
      className={`button game-selection-button ${isActive ? 'game-tile-active' : 'game-tile-inactive'} ${isSelected ? 'game-tile-selected' : ''}`}
      onClick={onClick}
      style={{ position: 'relative' }}
    >
      {game.premium && (
        <GamePremiumBadge isMonthlyFree={showMonthlyFree} />
      )}
      <div className="game-tile-content">
        <div className="game-title-row">
          <span className="game-icon">{game.icon}</span>
          <h2 className="game-title">
            {game.name}
          </h2>
        </div>
        <p className="game-description">
          {game.description}
        </p>
      </div>
      {isActive && (
        <div className="game-tile-active-content">
          {/* Image Slider */}
          {blitzImages.length > 0 && (
          <div className="game-tile-image-slider">
            {blitzImages.map((img, idx) => (
              <img
                key={idx}
                src={img}
                alt={t('lobby.gameSelection.blitzGameplayAlt', { index: idx + 1 })}
                className={idx === imageIndex ? 'active' : ''}
              />
            ))}
          </div>
          )}
          <div className="game-description-carousel">
            <div className="carousel-content">
              {game.extendedDescription} • {game.extendedDescription} • {game.extendedDescription} • {game.extendedDescription} • 
            </div>
          </div>
          {votes > 0 && (
            <span className="game-tile-votes">
              {votes} {t('common.vote', { count: votes })}
            </span>
          )}
        </div>
      )}
    </button>
  );
};

const GameSelection: React.FC<GameSelectionProps> = ({ roomName, currentGameType, votes = {}, categoryVotes = {}, players = [], teams, onSelectGame, onStartGame, socket, isHost = false, myPlayerId, settings, onGoHome, hostIsPlaying = false, onHostIsPlayingChange, isPremium = false, onJoinRoom }) => {
  const { t, i18n } = useTranslation();
  const GAMES = useMemo(
    () => translateGames(getGamesForRoom(isPremium)),
    [isPremium, i18n.language]
  );
  // All hooks must be called before any conditional returns
  const [myVote, setMyVote] = useState<string | null>(null);
  // Track the game type the user selected in the UI (even if backend rejected it)
  // This is critical for premium game blocking - we need to know what user selected
  const [selectedGameType, setSelectedGameType] = useState<string | undefined>(currentGameType);
  const lastBackendGameTypeRef = useRef<string | undefined>(currentGameType);
  
  // Update selectedGameType when currentGameType changes (backend accepted a change)
  // Only sync if currentGameType actually changed from what we last saw
  // This ensures we don't overwrite user's selection when backend rejects premium games
  useEffect(() => {
    if (currentGameType && currentGameType !== lastBackendGameTypeRef.current) {
      console.log('🔄 currentGameType changed (backend accepted), updating selectedGameType', {
        currentGameType,
        previousBackendGameType: lastBackendGameTypeRef.current,
        previousSelectedGameType: selectedGameType
      });
      lastBackendGameTypeRef.current = currentGameType;
      // Only update if backend actually accepted the change (game-type-changed was emitted)
      setSelectedGameType(currentGameType);
    }
  }, [currentGameType]);
  const [myCategoryVotes, setMyCategoryVotes] = useState<string[]>([]);
  // Track if player has already voted (from sessionStorage) to prevent duplicates after refresh
  const [hasVotedBefore, setHasVotedBefore] = useState<boolean>(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showStatistics, setShowStatistics] = useState(false);
  const [currentMenuPage, setCurrentMenuPage] = useState<MenuPage>(null);
  const { showToast } = useToast();
  // Image slider state for blitz
  const [imageIndex, setImageIndex] = useState(0);
  // Host player info state
  const [hostPlayerName, setHostPlayerName] = useState('');
  const [hostAvatar, setHostAvatar] = useState(avatars[0]);
  const [showHostPlayerForm, setShowHostPlayerForm] = useState(false);
  const [showQRModal, setShowQRModal] = useState(false);
  const [premiumButtonAnim, setPremiumButtonAnim] = useState<'zoom' | 'jump' | 'shake' | null>(null);
  const premiumAnimIndexRef = useRef(0);

  const PREMIUM_BUTTON_ANIMS = ['zoom', 'jump', 'shake'] as const;
  const PREMIUM_ANIM_DURATION_MS = 800;
  const PREMIUM_ANIM_INTERVAL_MS = 10000;
  
  // Blitz gameplay images
  const blitzImages = [
    '/assets/games/blitz/blitz-gameplay-1.png',
    '/assets/games/blitz/blitz-gameplay-2.png',
    '/assets/games/blitz/blitz-gameplay-3.png',
    '/assets/games/blitz/blitz-gameplay-4.png',
    '/assets/games/blitz/blitz-gameplay-5.png',
    '/assets/games/blitz/blitz-gameplay-6.png',
  ];
  
  // Check if host is currently in the players list
  const hostInPlayersList = players.some(p => p.id === myPlayerId);

  // Map app variant to game type for premium page
  const getGameType = (): 'blitz' | 'team' | 'family' | 'party' => {
    const variantMap: Record<string, 'blitz' | 'team' | 'family' | 'party'> = {
      blitz: 'blitz',
      team: 'team',
      family: 'family',
      party: 'party',
      new: 'party',
      coop: 'blitz',
    };
    return variantMap[appVariant] || 'blitz';
  };

  const gameLinks = getMenuGameLinks(getGameType());
  
  const menuItems = [
    {
      id: 'play-another-game-header',
      label: t('lobby.menu.playAnotherGamePack'),
      isSectionHeader: true,
    },
    ...gameLinks.map(link => ({
      id: link.id,
      label: getGameLinkName(link.id),
      href: link.url,
    })),
    {
      id: 'menu-divider',
      isDivider: true,
    },
    {
      id: 'menu-section-header',
      label: t('lobby.menu.sectionHeader'),
      isSectionHeader: true,
    },
    {
      id: 'how-to-play',
      label: t('common.howToPlay'),
      onClick: () => setCurrentMenuPage('how-to-play'),
    },
    {
      id: 'premium',
      label: t('common.premium'),
      onClick: () => setCurrentMenuPage('premium'),
    },
    {
      id: 'support',
      label: t('common.support'),
      onClick: () => setCurrentMenuPage('support'),
    },
  ];

  // Auto-rotate images in the slider
  useEffect(() => {
    if (config.appId === 'blitz' && isHost) {
      const interval = setInterval(() => {
        setImageIndex((prev) => (prev + 1) % blitzImages.length);
      }, 5000); // Change image every 5 seconds
      return () => clearInterval(interval);
    }
  }, [isHost, blitzImages.length]);

  useEffect(() => {
    if (!isHost || isPremium || !onJoinRoom) return;

    let clearAnimTimer: ReturnType<typeof setTimeout> | undefined;

    const runNextAnim = () => {
      const anim = PREMIUM_BUTTON_ANIMS[premiumAnimIndexRef.current % PREMIUM_BUTTON_ANIMS.length];
      premiumAnimIndexRef.current += 1;
      setPremiumButtonAnim(anim);
      clearAnimTimer = setTimeout(() => setPremiumButtonAnim(null), PREMIUM_ANIM_DURATION_MS);
    };

    runNextAnim();
    const interval = setInterval(runNextAnim, PREMIUM_ANIM_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      if (clearAnimTimer) clearTimeout(clearAnimTimer);
    };
  }, [isHost, isPremium, onJoinRoom]);
  
  // Sync hostIsPlaying state with whether host is actually in players list
  useEffect(() => {
    if (isHost && hostInPlayersList && !hostIsPlaying && !showHostPlayerForm) {
      // Host is in players list but hostIsPlaying is false - sync it
      if (onHostIsPlayingChange) {
        onHostIsPlayingChange(true);
      }
    } else if (isHost && !hostInPlayersList && hostIsPlaying) {
      // Host is NOT in players list but hostIsPlaying is true - sync it
      if (onHostIsPlayingChange) {
        onHostIsPlayingChange(false);
      }
    }
  }, [isHost, hostInPlayersList, hostIsPlaying, showHostPlayerForm, onHostIsPlayingChange]);
  
  const handleHostIsPlayingToggle = () => {
    if (!hostIsPlaying && !showHostPlayerForm) {
      // Enabling - show modal to enter name/avatar
      setShowHostPlayerForm(true);
    } else if (hostIsPlaying || showHostPlayerForm) {
      // Disabling - remove host from player list
      if (hostInPlayersList) {
        // Emit host-leave-as-player to remove from players without leaving room
        socket.emit('host-leave-as-player', { roomName });
      }
      setShowHostPlayerForm(false);
      // Clear session storage for player info
      RoomSessionStorage.clearPlayerData();
      if (onHostIsPlayingChange) {
        onHostIsPlayingChange(false);
      }
    }
  };
  
  const handleCloseHostPlayerModal = () => {
    setShowHostPlayerForm(false);
  };
  
  const handleHostJoinAsPlayer = () => {
    if (!hostPlayerName.trim()) {
      showToast(t('toasts.enterName'), 'warning');
      return;
    }
    
    // Save player info for reconnection
    RoomSessionStorage.setPlayerName(hostPlayerName.trim());
    RoomSessionStorage.setAvatar(hostAvatar);
    
    // Join as player
    socket.emit('join-room', {
      roomName,
      playerName: hostPlayerName.trim(),
      avatar: hostAvatar,
    });
    
    setShowHostPlayerForm(false);
    if (onHostIsPlayingChange) {
      onHostIsPlayingChange(true);
    }
  };
  
  const handleCancelHostJoin = () => {
    setShowHostPlayerForm(false);
  };

  // Listen for votes-updated to sync myVote
  useEffect(() => {
    const handleVotesUpdated = (data: { roomName: string; votes: Record<string, number> }) => {
      // We can't directly determine which vote is ours from the counts
      // But we can track it locally when we vote
    };

    const handleCategoryVotesUpdated = (data: { 
      roomName: string; 
      categoryVotes: Record<string, number>;
      playerCategoryVotes?: string[];
      playerId?: string;
    }) => {
      // Update our own category votes if this update is for us
      if (data.playerId === myPlayerId && data.playerCategoryVotes) {
        setMyCategoryVotes(data.playerCategoryVotes);
        // Save to sessionStorage
        if (currentGameType) {
          RoomSessionStorage.setCategoryVotes(roomName, currentGameType, data.playerCategoryVotes);
        }
      }
    };

    const handleCategoryVoteError = (error: { message: string }) => {
      showToast(translateBackendError(error.message), 'warning');
    };

    const handleGameTypeChanged = (data: { roomName: string; gameType: string }) => {
      // Reset player's category votes when game type changes
      console.log('Game type changed, resetting category votes');
      setMyCategoryVotes([]);
      // Clear sessionStorage for old game type
      if (currentGameType) {
        RoomSessionStorage.clearCategoryVotes(roomName, currentGameType);
      }
    };

    socket.on('votes-updated', handleVotesUpdated);
    socket.on('category-votes-updated', handleCategoryVotesUpdated);
    socket.on('category-vote-error', handleCategoryVoteError);
    socket.on('game-type-changed', handleGameTypeChanged);
    return () => {
      socket.off('votes-updated', handleVotesUpdated);
      socket.off('category-votes-updated', handleCategoryVotesUpdated);
      socket.off('category-vote-error', handleCategoryVoteError);
      socket.off('game-type-changed', handleGameTypeChanged);
    };
  }, [socket, showToast, myPlayerId, roomName, currentGameType]);

  // Check sessionStorage to prevent duplicate votes after refresh
  useEffect(() => {
    if (currentGameType && !isHost) {
      const savedVotes = RoomSessionStorage.getCategoryVotes(roomName, currentGameType);
      // If we have saved votes in sessionStorage, player has voted before
      // This prevents duplicate votes after page refresh (new playerId)
      if (savedVotes.length > 0) {
        setHasVotedBefore(true);
        // Don't restore votes to UI - they belong to the old playerId
        // But prevent new votes to avoid duplicates
      } else {
        setHasVotedBefore(false);
      }
    }
  }, [currentGameType, roomName, isHost]);

  // Show menu pages - must be after all hooks
  if (currentMenuPage === 'how-to-play') {
    return (
      <MenuPageWrapper
        title={t('common.howToPlay')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <HowToPlayPage />
      </MenuPageWrapper>
    );
  }

  if (currentMenuPage === 'premium') {
    return (
      <MenuPageWrapper
        title={t('common.premium')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <PremiumPage gameType={getGameType()} />
      </MenuPageWrapper>
    );
  }

  if (currentMenuPage === 'support') {
    return (
      <MenuPageWrapper
        title={t('common.support')}
        onMenuClick={setCurrentMenuPage}
        onBack={() => setCurrentMenuPage(null)}
        gameType={getGameType()}
      >
        <HelpPage />
      </MenuPageWrapper>
    );
  }

  const handleGameSelect = (gameType: string) => {
    console.log('🎯 handleGameSelect called', { gameType, isHost, isPremium, roomName });

    const selectedGame = ALL_GAMES.find((g) => g.id === gameType);
    const requiresPremium =
      !isPremium &&
      selectedGame?.premium === true &&
      !isMonthlyFreeGame(gameType);

    if (isHost && requiresPremium) {
      setSelectedGameType(gameType);
      setCurrentMenuPage('premium');
      return;
    }
    
    // Track the selected game type locally (even if backend rejects it)
    // This is critical - we need to know what the user selected even if backend rejects it
    setSelectedGameType(gameType);
    console.log('📝 selectedGameType updated to:', gameType);
    
    if (isHost) {
      // Host can change game type directly
      // Backend will validate and reject premium games in non-premium rooms
      // If backend rejects, it won't emit game-type-changed, so selectedGameType will remain
      console.log('📤 Emitting change-game-type', { roomName, gameType });
      socket.emit('change-game-type', { roomName, gameType });
      onSelectGame(gameType);
    } else {
      // Non-host players vote for games
      handleVote(gameType);
    }
  };

  const handleVote = (gameType: string) => {
    if (socket && roomName) {
      // Allow changing vote - just emit the new vote
      socket.emit('vote-for-game', { roomName, gameType });
      setMyVote(gameType);
    }
  };

  const gameTypeForTeams = selectedGameType || currentGameType;
  const showTeamAssign = gameUsesTeams(gameTypeForTeams);
  const teamStartBlock = getTeamStartBlockReason(players, gameTypeForTeams);

  const handleSetTeam = (playerId: string, teamId: TeamId | null) => {
    if (!socket || !roomName) return;
    socket.emit('set-player-team', { roomName, playerId, teamId });
  };

  const handleCategoryVote = (category: string) => {
    if (socket && roomName && currentGameType) {
      // Prevent duplicate votes if player has already voted (from sessionStorage)
      if (hasVotedBefore) {
        showToast(t('toasts.alreadyVotedCategories'), 'warning');
        return;
      }
      
      // Toggle category vote
      const isCurrentlyVoted = myCategoryVotes.includes(category);
      
      // Optimistically update local state
      let newVotes: string[];
      if (isCurrentlyVoted) {
        newVotes = myCategoryVotes.filter(c => c !== category);
      } else if (myCategoryVotes.length < 3) {
        newVotes = [...myCategoryVotes, category];
      } else {
        showToast(t('toasts.maxCategoryVotes'), 'warning');
        return;
      }
      
      setMyCategoryVotes(newVotes);
      // Save to sessionStorage immediately
      RoomSessionStorage.setCategoryVotes(roomName, currentGameType, newVotes);
      
      socket.emit('vote-for-category', { roomName, category });
    }
  };


  return (
    <div 
      className={`app ${isHost ? 'host-game-selection' : 'player-game-selection'}`}
    >
      {isHost && <BgAnimate speed={2.0} delayBetweenCycles={1000} gameType={currentGameType} />}
      <div style={{ position: 'relative', zIndex: 1 }}>
        <AppHeader
          logo={null}
          isPremium={isPremium}
          leftButton={onGoHome ? {
            icon: HeaderIcons.back,
            onClick: onGoHome,
            tooltip: t('common.backToHome')
          } : undefined}
          rightButton={undefined}
          rightContent={isHost ? (
            <div className="lobby-toolbar">
              <button
                type="button"
                data-testid="statistics-modal-trigger"
                onClick={() => setShowStatistics(true)}
                className="lobby-toolbar-btn"
                title={t('lobby.gameSelection.viewStatistics')}
              >
                <span className="lobby-toolbar-btn__icon"><AppIcon name="barChart" /></span>
              </button>
              <button
                type="button"
                data-testid="game-configuration-button"
                onClick={() => setShowSettings(true)}
                className="lobby-toolbar-btn"
                title={t('lobby.gameSelection.gameConfiguration')}
              >
                <span className="lobby-toolbar-btn__icon"><AppIcon name="settings" /></span>
              </button>
              <BurgerMenu items={menuItems} />
            </div>
          ) : undefined}
        />
      <div className="container">
        {isHost ? (
          // Host sees game selection buttons
          <div style={{ marginTop: '0', width: '100%' }}>
            {/* Choose Game carousel — primary focus at top */}
            <div className="choose-game-section">
              <div className="choose-game-header">
                <div className="choose-game-header-line"></div>
                <h2 className="choose-game-header-text">
                  {t('lobby.gameSelection.chooseGame')}
                </h2>
                <div className="choose-game-header-line"></div>
              </div>
              
              <div className="choose-game-carousel-wrap">
                <GameCarousel
                  games={GAMES}
                  currentGameType={selectedGameType || currentGameType}
                  votes={votes}
                  onSelectGame={handleGameSelect}
                  onPremiumClick={() => setCurrentMenuPage('premium')}
                  isPremiumRoom={isPremium}
                />
              </div>
            </div>

            {/* QR / players / categories row */}
            <div className="host-main-layout lobby-top-row">
              {/* Left Column: QR Code */}
              <div className="host-qr-column">
                <div className="host-qr-layout">
                  <div className="host-qr-layout__code">
                    <div className="host-qr-code-wrapper">
                      <QRCodeDisplay
                        roomName={roomName}
                        size={160}
                        showLabel={false}
                        containerStyle={{
                          padding: 0,
                          backgroundColor: 'transparent',
                          border: 'none',
                          borderRadius: 0,
                          width: '100%',
                        }}
                        innerStyle={{
                          padding: 0,
                          borderRadius: 0,
                          backgroundColor: 'transparent',
                          width: '100%',
                        }}
                      />
                      <button
                        type="button"
                        className="host-qr-zoom-button host-qr-zoom-button--overlay"
                        onClick={() => setShowQRModal(true)}
                        aria-label={t('lobby.gameSelection.zoomQrCode')}
                      >
                        <AppIcon name="zoomIn" />
                      </button>
                    </div>
                  </div>

                  <div className="host-qr-layout__details">
                    <div className="host-qr-room-code-top">
                      <div className="host-qr-room-code-label-row">
                        <span>{t('lobby.gameSelection.roomCodeLabel')}</span>
                        <RoomCodeDisplay code={roomName} />
                      </div>
                    </div>
                    {isPremium && (
                      <div className="host-qr-premium-label">
                        {t('lobby.gameSelection.premiumRoomLabel')}
                      </div>
                    )}
                    {onJoinRoom && !isPremium && (
                      <div className="host-qr-premium-actions">
                        <button
                          type="button"
                          className={`host-qr-premium-card host-qr-premium-card--primary${
                            premiumButtonAnim ? ` host-qr-premium-card--anim-${premiumButtonAnim}` : ''
                          }`}
                          onClick={() => setCurrentMenuPage('premium')}
                        >
                          <span className="host-qr-premium-card__row">
                            <AppIcon name="premium" fontSize="small" />
                            {t('lobby.gameSelection.getPremium')}
                          </span>
                          <span className="host-qr-premium-card__sub">{t('lobby.gameSelection.unlockFromPrice')}</span>
                        </button>
                        <button
                          type="button"
                          className="host-qr-premium-card host-qr-premium-card--secondary"
                          onClick={onJoinRoom}
                        >
                          <span className="host-qr-premium-card__row">{t('lobby.gameSelection.haveCode')}</span>
                          <span className="host-qr-premium-card__sub">{t('lobby.gameSelection.enterPremiumCode')}</span>
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="host-qr-layout__footer">
                    <p className="host-qr-share-hint">
                      <AppIcon name="tv" fontSize="small" />
                      {t('lobby.gameSelection.shareHint')}
                    </p>
                    <div className={`host-playing-toggle host-qr-playing-toggle ${hostIsPlaying ? 'active' : ''}`}>
                    <div
                      className="host-playing-toggle-header"
                      onClick={handleHostIsPlayingToggle}
                    >
                      <div className="host-playing-toggle-content">
                        <div className="host-playing-toggle-label">
                          <AppIcon name="gamepad" fontSize="small" />
                          <span>{t('lobby.gameSelection.playAsHostOnMobile')}</span>
                        </div>
                      </div>
                      <div className={`host-playing-toggle-switch ${hostIsPlaying ? 'active' : ''}`}>
                        <div className="host-playing-toggle-switch-thumb" />
                      </div>
                    </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Middle Column: Players List / Teams */}
              <div className="host-players-column">
                {showTeamAssign ? (
                  <TeamAssignPanel
                    players={players}
                    teams={teams}
                    canAssignAny={!!isHost}
                    myPlayerId={myPlayerId}
                    onSetTeam={handleSetTeam}
                  />
                ) : (
                  <PlayerListSection
                    players={players}
                    variant="compact"
                    collapsible
                    maxVisible={4}
                    title={<> <AppIcon name="people" fontSize="small" /> {t('common.players', { count: players.length })}</>}
                  />
                )}
              </div>

              {/* Right Column: Category votes (matches players height) */}
              <div className="host-right-column host-categories-column">
                <CategoryResults
                  gameType={currentGameType || ''}
                  categoryVotes={categoryVotes}
                  collapsible
                  maxVisible={5}
                />
              </div>
            </div>
          </div>
        ) : (
          // Non-host players: same structure as host (trivia/blitz) - full-width 3-column layout
          <div style={{ marginTop: '0', width: '100%' }}>
            <div className="host-main-layout lobby-top-row">
              {/* Left column: Room code + Waiting message (+ Selected Game on mobile/tablet to fill space) */}
              <div className="host-qr-column">
                <div className="host-qr-room-code-top">
                  <div className="host-qr-room-code-label-row">
                    <span>{t('lobby.gameSelection.roomCodeLabel')}</span>
                    <RoomCodeDisplay code={roomName} />
                  </div>
                </div>
                <div className="waiting-message" style={{ marginTop: '0.5rem', marginBottom: 0 }}>
                  <p><AppIcon name="hourglass" fontSize="small" /> {t('lobby.gameSelection.waitingForHost')}</p>
                </div>
                {currentGameType && (
                  <div className="selected-game-box player-selected-game-below-waiting">
                    <div className="selected-game-header">
                      <p className="selected-game-label">{t('lobby.gameSelection.selectedGame')}</p>
                      <div className="selected-game-icon-name">
                        <span className="selected-game-icon">{GAMES.find(g => g.id === currentGameType)?.icon}</span>
                        <span className="selected-game-name">
                          {GAMES.find(g => g.id === currentGameType)?.name}
                        </span>
                      </div>
                    </div>
                    {GAMES.find(g => g.id === currentGameType)?.extendedDescription && (
                      <div className="selected-game-description-carousel">
                        <div className="carousel-content">
                          {GAMES.find(g => g.id === currentGameType)?.extendedDescription} • {GAMES.find(g => g.id === currentGameType)?.extendedDescription} • {GAMES.find(g => g.id === currentGameType)?.extendedDescription} • {GAMES.find(g => g.id === currentGameType)?.extendedDescription} •
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Middle column: Players list / Teams (same as host) */}
              <div className="host-players-column">
                {showTeamAssign ? (
                  <TeamAssignPanel
                    players={players}
                    teams={teams}
                    canAssignAny={false}
                    myPlayerId={myPlayerId}
                    onSetTeam={handleSetTeam}
                  />
                ) : (
                  <PlayerListSection
                    players={players}
                    variant="compact"
                    collapsible
                    maxVisible={4}
                    title={<> <AppIcon name="people" fontSize="small" /> {t('common.players', { count: players.length })}</>}
                  />
                )}
              </div>

              {/* Right column: Selected game + Category vote (same as host right column) */}
              <div className="host-right-column">
                {currentGameType && (
                  <div className="selected-game-box player-selected-game-in-column player-selected-game-desktop-only">
                    <div className="selected-game-header">
                      <p className="selected-game-label">{t('lobby.gameSelection.selectedGame')}</p>
                      <div className="selected-game-icon-name">
                        <span className="selected-game-icon">{GAMES.find(g => g.id === currentGameType)?.icon}</span>
                        <span className="selected-game-name">
                          {GAMES.find(g => g.id === currentGameType)?.name}
                        </span>
                      </div>
                    </div>
                    {GAMES.find(g => g.id === currentGameType)?.extendedDescription && (
                      <div className="selected-game-description-carousel">
                        <div className="carousel-content">
                          {GAMES.find(g => g.id === currentGameType)?.extendedDescription} • {GAMES.find(g => g.id === currentGameType)?.extendedDescription} • {GAMES.find(g => g.id === currentGameType)?.extendedDescription} • {GAMES.find(g => g.id === currentGameType)?.extendedDescription} •
                        </div>
                      </div>
                    )}
                  </div>
                )}
                {currentGameType && gameHasCategories(currentGameType) && (
                  <CategoryVote
                    gameType={currentGameType}
                    myCategoryVotes={myCategoryVotes}
                    categoryVotes={categoryVotes}
                    onCategoryVote={handleCategoryVote}
                    maxVotes={3}
                    disabled={hasVotedBefore}
                  />
                )}
              </div>
            </div>
          </div>
        )}


      </div>
      {/* Fixed footer button for host */}
      {isHost && (
        <Footer>
          <div className="lobby-ready-footer">
            <div className="lobby-ready-footer__info">
              <span className="lobby-ready-footer__mascot" aria-hidden><AppIcon name="wavingHand" /></span>
              <div className="lobby-ready-footer__copy">
                <strong className="lobby-ready-footer__title">{t('lobby.gameSelection.readyTitle')}</strong>
                <p className="lobby-ready-footer__hint">
                  {t('lobby.gameSelection.readyHint')}
                </p>
              </div>
            </div>
            <button
              className="button start-game-button"
              onClick={() => {
                const gameTypeToStart = selectedGameType || currentGameType;
                if (gameUsesTeams(gameTypeToStart) && teamStartBlock) {
                  const toastKey =
                    teamStartBlock === 'unassigned'
                      ? 'lobby.teams.startNeedAssign'
                      : teamStartBlock === 'unbalanced'
                        ? 'lobby.teams.startNeedBalance'
                        : 'lobby.teams.startNeedPlayers';
                  showToast(t(toastKey), 'warning');
                  return;
                }
                onStartGame(gameTypeToStart);
              }}
              disabled={
                players.length === 0 ||
                !(selectedGameType || currentGameType) ||
                (((selectedGameType || currentGameType) === 'collaborative-puzzle' || (selectedGameType || currentGameType) === 'story-chain') && players.length < 2) ||
                (showTeamAssign && !!teamStartBlock)
              }
            >
              {t('lobby.gameSelection.startGame', {
                count: players.length,
                playerLabel: t('common.player', { count: players.length }),
              })}
            </button>
          </div>
        </Footer>
      )}
      {isHost && (
        <>
          <SettingsModal
            isOpen={showSettings}
            onClose={() => setShowSettings(false)}
            socket={socket}
            roomName={roomName}
            currentSettings={settings}
          />
          <StatisticsModal
            isOpen={showStatistics}
            onClose={() => setShowStatistics(false)}
            roomName={roomName}
          />
        </>
      )}
      {/* QR Code Zoom Modal */}
      <Modal
        isOpen={showQRModal}
        onClose={() => setShowQRModal(false)}
        contentStyle={{
          maxWidth: '95%',
          maxHeight: '95%',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: '3rem',
          gap: '1.5rem',
        }}
      >
        <div className="qr-modal-room-code">
          {t('lobby.gameSelection.qrModalRoom', { roomName })}
        </div>
        <QRCodeDisplay
          roomName={roomName}
          size={600}
          showLabel={false}
          containerStyle={{
            padding: 0,
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: 0,
          }}
        />
        <div className="qr-modal-scan-text">
          {t('lobby.gameSelection.qrModalScan')}
        </div>
      </Modal>
      
      {/* Host Player Form Modal */}
      <Modal
        isOpen={showHostPlayerForm && !hostIsPlaying}
        onClose={handleCloseHostPlayerModal}
        contentStyle={{
          maxWidth: '500px',
          width: '100%',
          padding: '2rem',
        }}
      >
        <div style={{ width: '100%' }}>
          <h2 style={{ 
            color: 'var(--text-primary)', 
            marginBottom: '1.5rem', 
            fontSize: '1.5rem',
            textAlign: 'center'
          }}>
            {t('lobby.gameSelection.joinAsPlayerTitle')}
          </h2>

          <label className="form-label">
            {t('common.yourName')}
          </label>
          <input
            type="text"
            className="input"
            placeholder={t('common.enterYourName')}
            value={hostPlayerName}
            onChange={(e) => setHostPlayerName(e.target.value)}
            style={{
              fontSize: '1rem',
              marginBottom: '1.5rem',
            }}
          />
          
          <AvatarSelector
            selectedAvatar={hostAvatar}
            onSelect={setHostAvatar}
            label={t('common.chooseAvatar')}
            gridColumns={4}
          />
          
          <div className="join-room-buttons">
            <button
              type="button"
              className="button join-room-back-button"
              onClick={handleCloseHostPlayerModal}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="button join-room-button"
              onClick={handleHostJoinAsPlayer}
            >
              {t('lobby.gameSelection.joinAsPlayer')}
            </button>
          </div>
        </div>
      </Modal>
      </div>
    </div>
  );
};

export default GameSelection;

